import { CellContext } from '@tanstack/react-table'

import CharacterAvatar from '../../components/CharacterAvatar'
import { Link } from 'react-router-dom'
import CharacterClassIcon from '../../components/CharacterClassIcon'
import { genericSorting } from './util'
import BlizzardIcon from '../../components/BlizzardIcon'
import { capitalize } from 'lodash'
import Timestamp from '../../components/Timestamp'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Check,
  Edit,
  MessageCircleMore,
  Pencil,
  TriangleAlert,
  X,
} from 'lucide-react'
import { TFunction } from 'i18next'
import TeamMemberContextMenu from '../../pages/TeamDashboard/subpages/TeamRoster/components/TeamMemberContextMenu'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import CharacterRoleIcon from '../../components/CharacterRoleIcon'
import UserBnetConnection from '../../pages/TeamDashboard/subpages/TeamRoster/components/UserBnetConnection'

export const name = {
  accessorKey: '0.characterReference.name',
  id: 'name',
  ...genericSorting<wowaudit.TeamMembership[]>('text', 'Name'),
  cell: (info: CellContext<wowaudit.TeamMembership[], unknown>) => {
    const characters = info.row.original
    const [main, ...alts] = characters

    const isOrphanAlt = !!main.teamRank.for_alts

    return (
      <div className='flex flex-col gap-1'>
        <Link
          to={main.characterReference.path}
          className={`inline-flex gap-2 items-center ${
            isOrphanAlt ? 'text-muted-foreground text-xs' : ''
          }`}
        >
          <CharacterAvatar
            character={main.characterReference}
            className={'h-6 w-6'}
          />
          {main.characterReference.name}
        </Link>

        {alts.map((alt) => (
          <div key={alt.id} className='h-6 text-xs'>
            <Link
              to={alt.characterReference.path}
              className='inline-flex gap-2 items-center ml-1 text-muted-foreground'
            >
              <span className='text-muted-foreground text-sm mr-2'>└</span>
              <CharacterAvatar
                character={alt.characterReference}
                className='h-4 w-4'
              />
              {alt.characterReference.name}
            </Link>
          </div>
        ))}
      </div>
    )
  },
}

export const refreshedAt = (
  t: TFunction,
  action?: (member: wowaudit.TeamMembership) => JSX.Element,
) => {
  return {
    accessorKey: '0.characterReference.refreshedAt',
    id: 'characterReference.refreshedAt',
    ...genericSorting<wowaudit.TeamMembership[]>(
      'alphanumeric',
      <div className='text-right'>Details updated</div>,
    ),
    cell: (info: CellContext<wowaudit.TeamMembership[], unknown>) => {
      const characters = info.row.original
      const isOrphanAlt = !!characters[0].teamRank.for_alts

      return (
        <div className='flex flex-col gap-1'>
          {characters.map((member) => (
            <div
              key={member.id}
              className={`text-right h-6 text-xs ${
                isOrphanAlt ? '' : 'first:text-sm'
              } flex items-center justify-end`}
            >
              {member.characterReference.gdprStatus === 'exists' ? (
                <Timestamp
                  time={member.characterReference.refreshedAt}
                  neverText={member.characterReference.status.replace('_', ' ')}
                />
              ) : (
                <div className='flex items-center justify-end gap-2'>
                  <Popover>
                    <PopoverTrigger asChild className='flex items-center gap-2'>
                      <Edit
                        size={14}
                        className='cursor-pointer opacity-70 hover:opacity-100'
                      />
                    </PopoverTrigger>
                    <PopoverContent
                      align='end'
                      className='text-sm text-center w-[9rem]'
                    >
                      <div className='mb-2'>{t('shared.mark-as')}</div>
                      <div className='flex flex-col gap-1'>
                        {action ? action(member) : null}
                      </div>
                    </PopoverContent>
                  </Popover>
                  <span className='italic text-red-500'>
                    {t(
                      `profile.character-gdpr-status.${member.characterReference.gdprStatus}`,
                    ).toLowerCase()}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )
    },
  }
}

export const role = {
  accessorKey: '0.role',
  id: 'role',
  size: 110,
  ...genericSorting<wowaudit.TeamMembership[]>('text', 'Role'),
  cell: (info: CellContext<wowaudit.TeamMembership[], unknown>) => {
    const characters = info.row.original
    const isOrphanAlt = !!characters[0].teamRank.for_alts

    return (
      <div className='flex flex-col gap-1'>
        {characters.map((member, i) => (
          <span
            key={member.id}
            className={`flex gap-2 items-center h-6 text-xs text-muted-foreground ${
              isOrphanAlt ? '' : 'first:text-sm first:text-foreground'
            }`}
          >
            <CharacterRoleIcon
              role={member.role}
              className={i === 0 ? '' : 'opacity-70'}
              size={i === 0 ? 'medium' : 'small'}
            />
            {capitalize(member.role)}
          </span>
        ))}
      </div>
    )
  },
}

export const note = (
  setEditing?: (member: wowaudit.TeamMembership) => void,
) => ({
  accessorKey: '0.note',
  id: 'note',
  size: 60,
  ...genericSorting<wowaudit.TeamMembership[]>('text', 'Note'),
  cell: (info: CellContext<wowaudit.TeamMembership[], unknown>) => {
    const characters = info.row.original

    return (
      <div className='flex flex-col gap-1'>
        {characters.map((member, i) => (
          <Tooltip key={member.id}>
            <TooltipTrigger
              onClick={() => (setEditing ? setEditing(member) : null)}
            >
              <div className='h-6 flex items-center justify-center truncate flex-shrink'>
                <MessageCircleMore
                  size={i === 0 ? 16 : 12}
                  className={member.note ? '' : 'text-muted-foreground/20'}
                />
              </div>
            </TooltipTrigger>
            <TooltipContent className='max-w-[50vw] whitespace-normal'>
              {member.note || (
                <span className='opacity-70 italic'>
                  {setEditing ? 'Click to add note' : 'No note'}
                </span>
              )}
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
    )
  },
})

export const itemLevel = (
  extendedDetails: Record<string, wowaudit.CharacterDetails>,
) => {
  return {
    accessorFn: (row: wowaudit.TeamMembership[]) => {
      const details = extendedDetails[row[0].characterReference.id]
      return details?.maxItemLevel || 0
    },
    id: 'itemLevel',
    size: 75,
    ...genericSorting<wowaudit.TeamMembership[]>('alphanumeric', 'iLvl', true),
    cell: (info: CellContext<wowaudit.TeamMembership[], unknown>) => {
      const characters = info.row.original
      const isOrphanAlt = !!characters[0].teamRank.for_alts

      return (
        <div className='flex flex-col gap-1'>
          {characters.map((member) => {
            const details = extendedDetails[member.characterReference.id]
            return (
              <div
                key={member.id}
                className={`transition-opacity duration-300 w-8 h-6 flex items-center text-xs text-muted-foreground ${
                  isOrphanAlt ? '' : 'first:text-sm first:text-foreground'
                } ${details ? 'opacity-100' : 'opacity-0'}`}
              >
                {details?.maxItemLevel || '-'}
              </div>
            )
          })}
        </div>
      )
    },
  }
}

export const rank = {
  accessorKey: '0.rank',
  id: 'rank',
  ...genericSorting<wowaudit.TeamMembership[]>('text', 'Rank'),
  cell: (info: CellContext<wowaudit.TeamMembership[], unknown>) => {
    const characters = info.row.original
    const isOrphanAlt = !!characters[0].teamRank.for_alts

    return (
      <div className='flex flex-col gap-1'>
        {characters.map((member) => (
          <span
            key={member.id}
            className={`h-6 text-xs ${
              isOrphanAlt ? '' : 'first:text-sm first:text-foreground'
            } text-muted-foreground flex items-center`}
          >
            {member.teamRank.name}
          </span>
        ))}
      </div>
    )
  },
}

export const spec = {
  accessorKey: '0.characterReference.characterClass',
  id: 'characterReference.characterClass',
  ...genericSorting<wowaudit.TeamMembership[]>('text', 'Class'),
  cell: (info: CellContext<wowaudit.TeamMembership[], unknown>) => {
    const characters = info.row.original
    const isOrphanAlt = !!characters[0].teamRank.for_alts

    return (
      <div className='flex flex-col gap-1'>
        {characters.map((member, i) => (
          <span
            key={member.id}
            className={`flex gap-2 items-center h-6 text-xs ${
              isOrphanAlt ? '' : 'first:text-sm first:text-foreground'
            } text-muted-foreground`}
          >
            {member.specDetails ? (
              <BlizzardIcon
                iconName={member.specDetails.icon}
                className={i === 0 ? '' : 'opacity-70'}
                size={i === 0 ? 'medium' : 'small'}
              />
            ) : (
              <CharacterClassIcon
                characterClass={member.characterReference.characterClass}
                className={i === 0 ? '' : 'opacity-70'}
                size={i === 0 ? 'medium' : 'small'}
              />
            )}
            <span>
              {member.specDetails?.name}{' '}
              {member.characterReference.characterClass}
            </span>
          </span>
        ))}
      </div>
    )
  },
}

export const offSpecs = {
  accessorKey: '0.offspecDetails',
  id: 'offspecDetails',
  size: 100,
  ...genericSorting<wowaudit.TeamMembership[]>('text', 'Off specs'),
  cell: (info: CellContext<wowaudit.TeamMembership[], unknown>) => {
    const characters = info.row.original
    const isOrphanAlt = !!characters[0].teamRank.for_alts

    return (
      <div className='flex flex-col gap-1'>
        {characters.map((member, i) => (
          <span
            key={member.id}
            className='h-6 text-xs first:text-sm flex items-center gap-1'
          >
            {member.offspecDetails.map((spec) => (
              <Tooltip key={spec.name}>
                <TooltipTrigger>
                  <BlizzardIcon
                    iconName={spec.icon}
                    className={i === 0 ? '' : 'opacity-70'}
                    size={i === 0 ? 'medium' : 'small'}
                  />
                </TooltipTrigger>
                <TooltipContent>{spec.name}</TooltipContent>
              </Tooltip>
            ))}
          </span>
        ))}
      </div>
    )
  },
}

export const connections = (
  usersById: Record<string, wowaudit.UserReference> | undefined,
  userIdsWithDuplicateMains: number[],
) => ({
  accessorKey: '0.connections',
  id: 'connections',
  size: 90,
  ...genericSorting<wowaudit.TeamMembership[]>('text', ''),
  cell: (info: CellContext<wowaudit.TeamMembership[], unknown>) => {
    const characters = info.row.original

    return (
      <div className='flex flex-col gap-1'>
        {characters.map((member, index) => (
          <div
            key={member.id}
            className={`h-6 flex items-start ${
              usersById === undefined ? 'opacity-0' : 'opacity-100'
            }`}
          >
            {index === 0 ? (
              <UserBnetConnection
                member={characters[0]}
                usersById={usersById}
                userIdsWithDuplicateMains={userIdsWithDuplicateMains}
              />
            ) : null}
          </div>
        ))}
      </div>
    )
  },
})
export const actions = {
  id: 'actions',
  size: 50,
  cell: (info: CellContext<wowaudit.TeamMembership[], unknown>) => {
    const characters = info.row.original

    return (
      <div className='flex flex-col gap-1'>
        {characters.map((member) => (
          <div key={member.id} className='h-6 flex items-center justify-center'>
            <TeamMemberContextMenu member={member} />
          </div>
        ))}
      </div>
    )
  },
}
